// utils/ldJson.ts
import type { AnyThing } from '@/schema'    // adjust to your actual path
import type { Ref } from 'vue'

/**
 * Builds JSON-LD head entries for:
 * - Organization
 * - WebApplication
 * - ItemList of products (if any)
 *
 * Accepts either a Ref<AnyThing[]> or a raw AnyThing[]
 */
export function generateLdJson(products: Ref<AnyThing[]> | AnyThing[]) {
  // Normalize to array
  const list: AnyThing[] = Array.isArray(products) ? products : products.value || []

  // Static Organization entry
  const orgJson = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://marketplace.rana.ai/#organization",
    name: "Rana Marketplace",
    url: "https://marketplace.rana.ai",
    sameAs: [
      "https://ranaengine.app",
      "https://marketplace.rana.ai",
      "https://rana.ai",
    ],
  }

  // Static WebApplication entry
  const appJson = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "@id": "https://marketplace.rana.ai/#webapplication",
    name: "Rana Marketplace Web App",
    url: "https://marketplace.rana.ai",
    applicationCategory: "BusinessApplication",
    operatingSystem: "All",
    browserRequirements: "Requires JavaScript",
    provider: { "@id": orgJson["@id"] },
    sameAs: orgJson.sameAs,
  }

  // Build ItemList if products exist
  let itemListJson: any = null
  if (list.length > 0) {
    const itemListElement = list.map((prod, i) => {
      // Base properties
      const item: Record<string, any> = { "@type": prod['@type'] }
      if (prod.id != null)        item.identifier    = prod.id
      if (prod.name)              item.name          = prod.name
      if (prod.description)       item.description   = prod.description
    
      // SameAs and URL if present
      if ((prod as any).sameAs)   item.sameAs        = (prod as any).sameAs
      if ((prod as any).url)      item.url           = (prod as any).url

      // Type-specific props
      switch (prod['@type']) {
        case 'Product':
          if ((prod as any).price != null) {
            item.offers = {
              "@type":        "Offer",
              price:          (prod as any).price,
              priceCurrency: (prod as any).priceCurrency ?? "USD",
            }
          }
          if ((prod as any).isFree === true) {
            item.isFree = true
          }
          break

        case 'Subscription':
          item.annualCost  = (prod as any).annualCost
          item.monthlyCost = (prod as any).monthlyCost
          if ((prod as any).price != null) {
            item.offers = {
              "@type":        "Offer",
              price:          (prod as any).price,
              priceCurrency: (prod as any).priceCurrency ?? "USD",
            }
          }
          break

        case 'WebSite':
          if ((prod as any).url) {
            item.url = (prod as any).url
          }
          break

        case 'WebApplication':
          if ((prod as any).browserRequirements) item.browserRequirements = (prod as any).browserRequirements
          if ((prod as any).offers)             item.offers             = (prod as any).offers
          break

        case 'DataDownload':
          if ((prod as any).encodingFormat) item.encodingFormat = (prod as any).encodingFormat
          if ((prod as any).contentUrl)     item.contentUrl     = (prod as any).contentUrl
          break

        case 'SoftwareSourceCode':
          if ((prod as any).programmingLanguage) item.programmingLanguage = (prod as any).programmingLanguage
          if ((prod as any).codeRepository)      item.codeRepository      = (prod as any).codeRepository
          if ((prod as any).exampleOfWork)       item.exampleOfWork       = (prod as any).exampleOfWork
          break

        case 'CreativeWork':
          /*if ((prod as any).programmingLanguage) item.programmingLanguage = (prod as any).programmingLanguage
          if ((prod as any).codeRepository)      item.codeRepository      = (prod as any).codeRepository
          if ((prod as any).exampleOfWork)       item.exampleOfWork       = (prod as any).exampleOfWork*/
          break

        // other types fall through
      }

      return {
        "@type": "ListItem",
        position: i + 1,
        item,
      }
    })

    itemListJson = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement,
    }
  }

  // Assemble separate script entries
  const scripts = [
    { type: "application/ld+json", innerHTML: JSON.stringify(orgJson,  null, 2) },
    { type: "application/ld+json", innerHTML: JSON.stringify(appJson,  null, 2) },
  ]
  if (itemListJson) {
    scripts.push({ type: "application/ld+json", innerHTML: JSON.stringify(itemListJson, null, 2) })
  }

  return { script: scripts }
}
